import { useState, useEffect } from 'react';
// import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { verifyEmail } from '@/lib/api';
import { CheckCircle2, AlertCircle, ScrollText } from 'lucide-react';
import { motion } from 'framer-motion';

export function VerifyEmail() {
    const [searchParams] = useSearchParams();
    // const navigate = useNavigate();
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const [isAgreed, setIsAgreed] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token || !email) {
            setError('認証リンクが無効です。');
        }
    }, [token, email]);

    const handleVerify = async () => {
        if (!token || !email || !isAgreed) return;

        setIsSubmitting(true);
        try {
            const res = await verifyEmail(token, email, isAgreed);
            localStorage.setItem('token', res.data.access_token);
            setSuccess(true);
            setTimeout(() => {
                // Force reload to update auth state
                window.location.href = '/';
            }, 3000);
        } catch (err: any) {
            console.error(err);
            let errorMessage = '認証に失敗しました。';
            if (err.response?.data?.detail) {
                if (typeof err.response.data.detail === 'string') {
                    errorMessage = err.response.data.detail;
                } else if (Array.isArray(err.response.data.detail)) {
                    errorMessage = err.response.data.detail.map((e: any) => e.msg).join(', ');
                } else {
                    errorMessage = JSON.stringify(err.response.data.detail);
                }
            }
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0c0a09] font-serif p-4 relative overflow-hidden">
                <div className="absolute inset-0 z-0 opacity-30 bg-[url('https://images.unsplash.com/photo-1541701494587-cb58502866ab?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center"></div>

                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-full max-w-md bg-[#1c1917] border border-[#d4af37] p-8 text-center relative z-10 shadow-2xl"
                >
                    <div className="flex justify-center mb-6">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: "spring" }}
                            className="w-20 h-20 bg-[#d4af37]/20 rounded-full flex items-center justify-center border border-[#d4af37]"
                        >
                            <CheckCircle2 className="w-10 h-10 text-[#fcd34d]" />
                        </motion.div>
                    </div>
                    <h2 className="text-2xl font-bold text-[#fcd34d] mb-4 tracking-widest">本人確認完了</h2>
                    <div className="h-px w-24 bg-[#d4af37] mx-auto my-6 opacity-50"></div>
                    <p className="text-[#d4af37]/80 leading-relaxed">
                        Maison Portalへようこそ。<br />
                        入居手続きが完了しました。<br />
                        まもなくロビーへご案内します...
                    </p>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0c0a09] font-serif p-4 relative overflow-hidden">
            <div className="absolute inset-0 z-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1463695973559-968b5774a8ac?q=80&w=2574&auto=format&fit=crop')] bg-cover bg-center"></div>

            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="w-full max-w-2xl bg-[#1c1917]/95 border border-[#d4af37] p-8 relative z-10 shadow-[0_0_50px_rgba(0,0,0,0.8)] backdrop-blur-sm"
            >
                <div className="text-center mb-8">
                    <ScrollText className="mx-auto text-[#d4af37] mb-4" size={40} />
                    <h1 className="text-2xl font-bold text-[#fcd34d] tracking-[0.2em]">最終確認</h1>
                    <p className="text-[#d4af37]/60 text-sm mt-2 tracking-widest">アカウントを有効化するには、利用規約への同意が必要です。</p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-950/30 border border-red-900/50 text-red-400 text-sm flex items-center gap-2 justify-center">
                        <AlertCircle size={20} />
                        {error}
                    </div>
                )}

                <div className="bg-[#292524] p-6 border border-[#d4af37]/30 h-[400px] overflow-y-auto mb-8 text-sm text-[#d4af37]/80 leading-relaxed scrollbar-thin scrollbar-thumb-[#d4af37]/30 scrollbar-track-[#1c1917]">
                    <h3 className="font-bold text-[#fcd34d] text-lg mb-6 text-center border-b border-[#d4af37]/30 pb-4">Maison Portal 利用規約</h3>

                    <div className="space-y-6">
                        <div className="p-3 bg-[#fcd34d]/10 border border-[#fcd34d]/30 text-[#fcd34d] text-xs">
                            ⚠️ 重要：本サービスをご利用になる前に、必ず本利用規約の全文をお読みください。本サービスの利用をもって、本利用規約の全てに同意したものとみなされます。
                        </div>

                        <section>
                            <h4 className="font-bold text-[#fcd34d] mb-2 border-l-2 border-[#d4af37] pl-2">第1条（総則）</h4>
                            <ol className="list-decimal pl-5 space-y-2">
                                <li>本利用規約（以下「本規約」といいます）は、Maison Portal（以下「本サービス」といいます）の提供者（以下「当社」といいます）が提供する全てのサービスの利用条件を定めるものです。</li>
                                <li>本サービスを利用するすべてのユーザー（以下「利用者」といいます）は、本規約に同意したものとみなされます。</li>
                                <li>本規約に同意いただけない場合、本サービスを利用することはできません。</li>
                                <li>当社は、利用者の事前の承諾を得ることなく、本規約を随時変更することができます。変更後の規約は、本サービス上に掲載された時点で効力を生じるものとします。</li>
                            </ol>
                        </section>

                        <section>
                            <h4 className="font-bold text-[#fcd34d] mb-2 border-l-2 border-[#d4af37] pl-2">第2条（定義）</h4>
                            <p className="mb-2">本規約において使用する用語の定義は、以下のとおりとします：</p>
                            <ol className="list-decimal pl-5 space-y-2">
                                <li><strong>「本サービス」</strong>：当社が運営する「Maison Portal」及びこれに関連する全てのサービス、機能、コンテンツを指します。</li>
                                <li><strong>「利用者」</strong>：本サービスを利用する全ての個人及び法人を指します。</li>
                                <li><strong>「登録情報」</strong>：利用者が本サービスの利用に際して登録した情報を指します。</li>
                                <li><strong>「投稿コンテンツ」</strong>：利用者が本サービス上に投稿、アップロード、送信した全ての情報、データ、テキスト、画像、動画、音声等を指します。</li>
                                <li><strong>「知的財産権」</strong>：著作権、商標権、特許権、実用新案権、意匠権その他の知的財産権を指します。</li>
                            </ol>
                        </section>

                        <section>
                            <h4 className="font-bold text-[#fcd34d] mb-2 border-l-2 border-[#d4af37] pl-2">第3条（利用登録）</h4>
                            <ol className="list-decimal pl-5 space-y-2">
                                <li>利用者は、本規約に同意の上、当社所定の方法により利用登録を申請し、当社がこれを承認することによって、利用登録が完了するものとします。</li>
                                <li>当社は、利用登録の申請者に以下の事由があると判断した場合、利用登録を承認しないことがあり、その理由について一切開示義務を負いません：
                                    <ul className="list-disc pl-5 mt-1 space-y-1 opacity-80">
                                        <li>利用登録の申請に際して虚偽の事項を届け出た場合</li>
                                        <li>本規約に違反したことがある者からの申請である場合</li>
                                        <li>未成年者等であり、法定代理人等の同意等を得ていなかった場合</li>
                                        <li>反社会的勢力等であると当社が判断した場合</li>
                                        <li>その他、当社が利用登録を相当でないと判断した場合</li>
                                    </ul>
                                </li>
                            </ol>
                        </section>

                        <section>
                            <h4 className="font-bold text-[#fcd34d] mb-2 border-l-2 border-[#d4af37] pl-2">第4条（アカウント情報の管理）</h4>
                            <ol className="list-decimal pl-5 space-y-2">
                                <li>利用者は、自己の責任において、本サービスに関するアカウント情報（ID、パスワード等）を適切に管理及び保管するものとします。</li>
                                <li>アカウント情報の管理不十分、使用上の過誤、第三者の使用等による損害の責任は利用者が負うものとし、当社は一切の責任を負いません。</li>
                            </ol>
                        </section>

                        <section>
                            <h4 className="font-bold text-[#fcd34d] mb-2 border-l-2 border-[#d4af37] pl-2">第5条（AI技術の使用）</h4>
                            <div className="bg-[#d4af37]/10 p-3 rounded border border-[#d4af37]/20">
                                <h5 className="font-bold mb-2 text-[#fcd34d]">重要事項</h5>
                                <ol className="list-decimal pl-5 space-y-2">
                                    <li>本サービスは、利用者の投稿コンテンツに対してAI（人工知能）技術を使用し、自動的に内容の分析、検閲、修正を行う場合があります。</li>
                                    <li>利用者は、本サービスの利用により、自己の投稿コンテンツがAIによって処理、変更されることに明示的に同意するものとします。</li>
                                    <li>AIによる修正は、コンテンツの品質向上、不適切な表現の改善、コミュニティガイドラインの遵守を目的としています。</li>
                                    <li>利用者は、AIによる修正後のコンテンツが自己の意図と異なる場合があることを理解し、これに異議を唱えないものとします。</li>
                                </ol>
                            </div>
                        </section>

                        <section>
                            <h4 className="font-bold text-[#fcd34d] mb-2 border-l-2 border-[#d4af37] pl-2">第7条（禁止事項）</h4>
                            <p className="mb-2">利用者は、本サービスの利用にあたり、以下の行為を行ってはなりません：</p>
                            <ol className="list-decimal pl-5 space-y-2">
                                <li>法令又は公序良俗に違反する行為</li>
                                <li>犯罪行為に関連する行為</li>
                                <li>過度にネガティブ、暴力的、または他者に不快感を与える表現を含む情報の投稿</li>
                                <li>本サービスの運営を妨害するおそれのある行為</li>
                                <li>他の利用者への嫌がらせや誹謗中傷</li>
                                <li>AIシステムの回避を試みる行為</li>
                            </ol>
                        </section>

                        <section>
                            <h4 className="font-bold text-[#fcd34d] mb-2 border-l-2 border-[#d4af37] pl-2">第8条（投稿コンテンツの取扱い）</h4>
                            <ol className="list-decimal pl-5 space-y-2">
                                <li>利用者は、投稿コンテンツについて、適法な権利を有していることを保証するものとします。</li>
                                <li>利用者は、投稿コンテンツについて、当社に対し、世界的、非独占的、無償での利用（使用、複製、配布、派生著作物の作成等）を許諾します。</li>
                                <li>当社は、本規約に違反する場合や不適切と判断した場合、事前の通知なく投稿コンテンツを削除または修正することができます。</li>
                            </ol>
                        </section>

                        <p className="text-center text-xs opacity-60 mt-8">
                            （以下、一般条項省略）<br />
                            詳細な全文は、登録後に設定画面から確認できます。
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 mb-8 p-4 bg-[#292524] border border-[#d4af37]/30 cursor-pointer hover:bg-[#292524]/80 transition-colors" onClick={() => setIsAgreed(!isAgreed)}>
                    <div className={`w-6 h-6 border transition-colors flex items-center justify-center ${isAgreed ? 'bg-[#d4af37] border-[#d4af37]' : 'border-[#d4af37]/50'}`}>
                        {isAgreed && <CheckCircle2 size={16} className="text-black" />}
                    </div>
                    <label className="text-sm font-medium cursor-pointer select-none text-[#d4af37] tracking-wider">
                        利用規約の内容を確認し、これに同意します。
                    </label>
                </div>

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleVerify}
                    className={`w-full h-14 font-serif font-bold text-lg uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 relative group overflow-hidden ${isAgreed && !isSubmitting ? 'bg-[#d4af37] hover:bg-[#ffe58f] text-black cursor-pointer shadow-[0_0_20px_rgba(212,175,55,0.4)]' : 'bg-[#292524] text-[#d4af37]/30 cursor-not-allowed border border-[#d4af37]/10'}`}
                    disabled={!isAgreed || isSubmitting || !!error}
                >
                    {isSubmitting ? (
                        <span className="animate-pulse">処理中...</span>
                    ) : (
                        <>
                            <span>同意して入居プロセスを完了する</span>
                        </>
                    )}
                </motion.button>
            </motion.div>
        </div>
    );
}
